import { AxiosResponse } from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { api } from 'api';
import {
  UserRole,
  AuthEmail,
  AuthGuest,
  AuthSignUp,
  VerifyCode,
  VerifyCodeType,
} from 'types';
import {
  clearAll,
  saveUser,
  getTokens,
  saveTokens,
  saveAuth0Id,
  clearAuth0Id,
  parseAxiosError,
} from 'utils';

import { setLoading } from '../loader';

import { clearAuth } from '.';
import { types, AuthLogout } from './types';

const isGuestAuth = (payload: AuthEmail | AuthGuest): payload is AuthGuest =>
  (payload as AuthGuest).guestId !== undefined;

export const login = createAsyncThunk(
  types.auth,
  async (payload: AuthEmail | AuthGuest, { rejectWithValue }) => {
    try {
      const isGuest = isGuestAuth(payload);

      const data = isGuest
        ? await api.auth.loginGuest(payload)
        : await api.auth.login(payload);

      const authData = data?.data?.data;
      const {
        user,
        auth0Id,
        mfaToken,
        mfaOptions,
        accessToken,
        refreshToken,
        signupVerifications,
      } = authData;

      const isLoggedIn =
        !signupVerifications?.length && !mfaOptions?.length && user;

      if (isLoggedIn && !isGuest && user.role !== UserRole.TenantUser) {
        throw new Error('Only customers can make a login');
      }

      if (auth0Id) {
        saveAuth0Id(auth0Id);
      } else {
        clearAuth0Id();
      }

      saveTokens({ refreshToken, token: accessToken || mfaToken });

      if (isLoggedIn) {
        saveUser(user);
      }

      return { isLoggedIn, mfaOptions, data: authData, signupVerifications };
    } catch (error) {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const signUp = createAsyncThunk(
  types.signup,
  async (payload: AuthSignUp, { rejectWithValue }) => {
    try {
      const data = await api.auth.signUp(payload);
      const authData = data?.data?.data;
      const {
        user,
        auth0Id,
        mfaToken,
        accessToken,
        refreshToken,
        signupVerifications,
      } = authData;

      if (auth0Id) {
        saveAuth0Id(auth0Id);
      } else {
        clearAuth0Id();
      }

      const isLoggedIn = !signupVerifications?.length && user;

      saveTokens({ refreshToken, token: accessToken || mfaToken });

      if (isLoggedIn) {
        saveUser(user);
      }

      return { isLoggedIn, data: authData, signupVerifications };
    } catch (error) {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const logout = createAsyncThunk(
  types.logout,
  async (values: AuthLogout, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      try {
        const { token, refreshToken } = getTokens();
        if (token && refreshToken) {
          await api.auth.logout({ refreshToken });
        }
      } catch (error: any) {
        console.error(error.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      clearAll();
      dispatch(setLoading(false));
      dispatch(clearAuth());
      const { resetApiState } = await import('store/utils');
      resetApiState(dispatch);
    }
  }
);

export const checkEmailVerification = createAsyncThunk(
  types.checkEmailVerification,
  async (_payload, { rejectWithValue }) => {
    const handleError = (error: unknown) => {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    };

    try {
      const data = await api.auth.checkEmailVerification();
      const user = data.data.data.user;
      if (user) {
        saveUser(user);
        return { user, isLoggedIn: true };
      } else {
        return { isLoggedIn: false };
      }
    } catch (error) {
      return handleError(error);
    }
  }
);

export const verifyCodes = createAsyncThunk(
  types.verifyCodes,
  async (payload: VerifyCode[], { rejectWithValue }) => {
    const handleError = (error: unknown) => {
      const errorMessage = parseAxiosError(error);
      return rejectWithValue(errorMessage);
    };

    const successVerifiedTypes: VerifyCodeType[] = [];
    const successfulResponses: AxiosResponse<any, any>[] = [];

    for (const item of payload) {
      try {
        const response = await api.auth.mfaVerify({
          code: item.code,
          type: item.type,
          oobCode: item.oobCode,
        });
        successfulResponses.push(response);
        successVerifiedTypes.push(item.type);
      } catch (error) {
        return handleError(error);
      }
    }

    const lastResponse = successfulResponses[successfulResponses.length - 1];

    const authData = lastResponse?.data?.data || {};
    const { user, accessToken, refreshToken, signupVerifications } = authData;

    const isLoggedIn = user !== undefined;

    if (accessToken && refreshToken) {
      saveTokens({ refreshToken, token: accessToken });
      clearAuth0Id();
    }

    if (isLoggedIn) {
      saveUser(user);
    }

    return {
      isLoggedIn,
      data: authData,
      signupVerifications,
      successVerifiedTypes,
    };
  }
);
